// In-memory stale-while-revalidate cache for salon/staff summary responses.
//
// Purpose: make re-opening a salon or professional page feel instant. On
// revisit we read the cached raw API payload synchronously, render
// immediately, and kick off a fresh fetch in the background. If the fresh
// response differs, state is updated silently. Same pattern (and the same
// reasoning) as marketplaceCache.js, which does this for the Explore feed.
//
// Scope: module-level, keyed by page identity ("salon:<slug>" or
// "staff:<id>"), so several recently-viewed pages can be warm at once.
// Deliberately NOT in localStorage - only useful while the tab is open,
// and the payload (full salon graph) is too big to hydrate on boot.
//
// Freshness: 5 minutes. Short enough that prices/availability shown from
// cache are never deeply stale, long enough that browse-back-browse always
// hits cache.
//
// Location sensitivity: an entry is tied to the lat/lng it was fetched with,
// because the payload carries a distance figure. If the caller's resolved
// location has moved materially (>0.05 deg, ~5 km) the entry is a miss.

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const LOCATION_THRESHOLD_DEG = 0.05; // ~5 km — matches marketplaceCache.js

const entries = new Map(); // key -> { data, timestamp, latitude, longitude }
const MAX_ENTRIES = 10; // browse sessions touch a handful of pages, not hundreds

/**
 * Return the cached raw summary response for `key` if it's fresh AND was
 * fetched close to `location`. Returns null on cache miss. Never throws.
 */
export const getSalonSummaryCache = (key, location) => {
  const entry = entries.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    entries.delete(key);
    return null;
  }

  if (
    location &&
    typeof location.latitude === 'number' &&
    typeof entry.latitude === 'number'
  ) {
    const latDelta = Math.abs(location.latitude - entry.latitude);
    const lngDelta = Math.abs(location.longitude - entry.longitude);
    if (latDelta > LOCATION_THRESHOLD_DEG || lngDelta > LOCATION_THRESHOLD_DEG) {
      return null;
    }
  }

  return entry.data;
};

/**
 * Store a raw summary response along with the location it was fetched with.
 * Overwrites any previous entry for the same key; evicts the oldest entry
 * once the cache is full.
 */
export const setSalonSummaryCache = (key, data, location) => {
  if (!key || !data) return;
  entries.delete(key); // re-insert so Map order reflects recency
  if (entries.size >= MAX_ENTRIES) {
    const oldest = entries.keys().next().value;
    entries.delete(oldest);
  }
  entries.set(key, {
    data,
    timestamp: Date.now(),
    latitude: location?.latitude,
    longitude: location?.longitude,
  });
};

/** Clear the cache. Exported for completeness (e.g. on logout). */
export const clearSalonSummaryCache = () => {
  entries.clear();
};
